import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MailService } from '../../services';
import axios from 'axios';
import { AlertModal, AlertType } from '../../components/layouts/alert-modal/alert-modal';
import { isValidCompanyName, isValidEmail, isValidName, sanitizeCompanyNameInput, sanitizeNameInput } from '../../shared/helpers';
import { ActivatedRoute } from '@angular/router';
import { PricingPlan } from '../../shared/models/pricing-plan';
import { PackageService } from '../../services/package-service/package-service';

export interface MessageTypeOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [CommonModule, FormsModule, AlertModal],
  templateUrl: './contact-us.html',
  styleUrl: './contact-us.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactUs implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private mailService = inject(MailService);
  private route = inject(ActivatedRoute);
  private packageService = inject(PackageService);

  formData = {
    name: '',
    email: '',
    message: '',
    companyName: '',
    messageType: 0,
    customMessageType: '',
    contactNumber: ''
  };

  loading = false;
  success = false;
  error = false;

  alertKey = 0;
  showAlert = false;
  alertMessage = "";
  alertTitle = "";
  alertType: AlertType = 'information';
  isLoading = false;

  previousEmail = '';
  previousName = '';
  previousCompany = '';
  previousContactNumber = '';

  errors: Record<string, string> = {};

  packages: PricingPlan[] = [];
  messageTypes: MessageTypeOption[] = [];

  ngOnInit() {
    const initPackages = this.packageService.getAllPackages();
    const initMessageTypes = initPackages.map((pkg, index) => ({
      label: pkg.title,
      value: index,
    }));

    const initMessageType = this.route.snapshot.queryParamMap.get('messageType') ?? "";

    const selectedMessageType = initMessageTypes.find(
      mt => mt.label === String(initMessageType)
    )?.value || 0;

    this.messageTypes = initMessageTypes;
    this.formData.messageType = selectedMessageType;
  }

  showAlertModal(title: string, message: string, loading = false, alertType: AlertType = 'information') {
    this.alertTitle = title;
    this.alertMessage = message;
    this.isLoading = loading;
    this.showAlert = true;
    this.alertType = alertType;

    this.cdr.detectChanges();
  }

  closeAlertModal() {
    this.showAlert = false;
    this.isLoading = false;
    this.alertTitle = "";
    this.alertMessage = "";
    this.alertType = 'information';

    this.cdr.detectChanges();
  }

  get emailInvalid(): boolean {
    return this.formData.email !== '' && !isValidEmail(this.formData.email);
  }

  get hasErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  // =========================
  // FIXED INPUT HANDLERS
  // =========================

  onEmailInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value === '' || /^[^\s@]*@?[^\s@]*\.?[^\s@]*$/.test(value)) {
      this.formData.email = value;
      this.previousEmail = value;
    } else {
      input.value = this.previousEmail;
      this.formData.email = this.previousEmail;
    }

    if (value !== '' && !isValidEmail(this.formData.email)) {
      this.errors['email'] = 'Please enter a valid email.';
    } else {
      delete this.errors['email'];
    }

    this.cdr.detectChanges();
  }

  onContactNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const sanitized = value.replace(/[^0-9+\-\s()]/g, '');

    input.value = sanitized;
    this.formData.contactNumber = sanitized;

    this.previousContactNumber = sanitized;

    if (value !== '' && sanitized.length < 7) {
      this.errors['contactNumber'] = 'Please enter a valid contact number.';
    } else {
      delete this.errors['contactNumber'];
    }

    this.cdr.detectChanges();
  }

  onNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const sanitized = sanitizeNameInput(value, this.previousName, 50);

    input.value = sanitized;
    this.formData.name = sanitized;

    if (sanitized !== this.previousName) {
      this.previousName = sanitized;
    }

    if (value !== '' && !isValidName(this.formData.name)) {
      this.errors['name'] = 'Please enter a valid name (letters, spaces, hyphens allowed).';
    } else {
      delete this.errors['name'];
    }
  }

  onCompanyInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const sanitized = sanitizeCompanyNameInput(value, this.previousCompany, 100);

    input.value = sanitized;
    this.formData.companyName = sanitized;

    if (sanitized !== this.previousCompany) {
      this.previousCompany = sanitized;
    }

    if (value !== '' && !isValidCompanyName(this.formData.companyName)) {
      this.errors['companyName'] = 'Please enter a valid company name (letters, numbers, & symbols allowed).';
    } else {
      delete this.errors['companyName'];
    }
  }

  // =========================
  // SUBMIT
  // =========================

  async onSubmit(): Promise<void> {
    this.loading = true;

    const selectedValue = this.formData.messageType;

    let selectedLabel: string;
    if (selectedValue == -1) {
      selectedLabel = this.formData.customMessageType?.trim() || 'Others';
    } else {
      const selectedType = this.messageTypes.find(mt => mt.value === Number(selectedValue));
      selectedLabel = selectedType?.label ?? 'Others';
    }

    const payload = {
      subject: `Service Inquiry`,
      name: this.formData.name,
      email: this.formData.email,
      message: this.formData.message,
      company_name: this.formData.companyName,
      package_name: selectedLabel,
      contact_number: this.formData.contactNumber,
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) =>
      formData.append(key, value as string)
    );

    await new Promise(resolve => setTimeout(resolve, 500));

    this.mailService.sendMail(formData)
      .then(response => {
        console.log('Success sending email:', response.data);
        this.showAlertModal("Success", "Your request has been submitted", false);
        this.loading = false;
        this.cdr.detectChanges();
      })
      .catch(error => {
        this.loading = false;
        this.cdr.detectChanges();

        this.showAlertModal(
          "Error",
          "Something went wrong, please try again later.",
          false,
          'error'
        );

        if (axios.isAxiosError(error)) {
          console.error('Error sending email:', error.response?.data);
          console.error('Status Code:', error.response?.status);
        } else {
          console.error('Unexpected error:', error);
        }
      });
  }
}